'use client';

import React, { useMemo } from 'react';
import { Button, PageHeader, Tag } from '@miso/design-system';
import { formatStructuredAddress } from '@/entities/visit-counsel/model/visit-counsel.types';
import {
  getIntakeDeparture,
  getIntakeArrival,
  getIntakePacking,
  getIntakeCustomerRequests,
  getIntakeMovingDate,
  hasIntakeMovingTypeRecorded,
} from '@/entities/visit-counsel/model/intake-helpers';
import { getIntakeChecklistDescription } from '@/features/visit-counsel/counsel-progress/model/getIntakeChecklistDescription';
import {
  SERVICE_SET_LABELS,
  type MovingItemView,
} from '@/entities/visit-counsel/model/room-observing.types';
import { useMovingItemSpecs } from '@/features/moving-item/fetch-specs';
import { SPACE_TYPE_LABELS } from '@/features/visit-counsel/counsel-progress/model/counsel-progress.constants';
import type {
  VisitCounselView,
  SketchingSession,
  SketchingSessionRoom,
  RoomObservingSession,
  AdditionalSpaceObservingSession,
} from '@/entities/visit-counsel/model/visit-counsel.types';
import type { MovingTypeSpecificationResponse } from '@/entities/visit-counsel/model/moving-type-intake.types';
import type { MovingItemRequest } from '@/entities/visit-counsel/model/room-observing.types';
import { buildMovingTypeIntakeReviewBlocks } from '@/features/visit-counsel/intake-form/model/movingTypeAttributeSummary';
import { hasObservingSessionMovingItemsConfirmed } from '@/entities/visit-counsel/model/observing-session-moving-items';
import { resolveRoomObservingSessionForSketchRoom } from '@/entities/visit-counsel/model/resolveRoomObservingSessionForSketchRoom';
import { formatSketchingRoomLabelSpaced } from '@/entities/visit-counsel/model/sketchRoomDisplayLabel';
import { isSingletonRoomType } from '@/entities/visit-counsel/model/recoverRoomType';
import { sortAdditionalSpaceObservingSessions } from '@/features/visit-counsel/counsel-progress/model/additionalSpaceObservingOrder';
import type { Spec } from '@/entities/moving-item/model/moving-item.types';

// ─── 상수 ────────────────────────────────────────────────────────────────────

const PACKING_LABELS: Record<string, string> = {
  FULL_PACKING: '전체 포장',
  PARTIAL_PACKING: '반포장',
  NO_PACKING: '일반(비포장)',
};

// ─── 데이터 헬퍼 ─────────────────────────────────────────────────────────────

function formatItemDisplay(item: MovingItemView, specMap: Map<string, Spec>): string {
  const spec = specMap.get(item.specId);
  if (!spec) return '알 수 없는 짐';
  const attrs = item.attributes ?? {};
  const filled = spec.attributes.filter((a) => attrs[a.key]);
  if (filled.length === 0) return spec.name;
  const parts = filled.map((a) => {
    const v = attrs[a.key];
    if (a.type === 'ENUM') return a.enumDisplayNames?.[v] ?? v;
    if (a.type === 'NUMBER') return `${v}${a.name ?? a.key}`;
    return v;
  });
  return `${spec.name} (${parts.join(' ')})`;
}

// ─── 시각 빌딩 블록 ──────────────────────────────────────────────────────────

/**
 * 회색 캔버스 위에 떠 있는 흰 카드. 모든 섹션이 동일한 카드 셸로 묶여 시각 계층이
 * 명확해진다 — 옛 버전은 단순 divider 만 사용해 정보 단위 식별이 어려웠다.
 */
function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <section className={`rounded-2xl bg-white border border-gray-100 shadow-sm ${className ?? ''}`}>
      {children}
    </section>
  );
}

/**
 * 카드 상단 헤더 — 아이콘 + 한국어 제목 + 우측 슬롯(상태/카운트 Tag 등).
 */
function CardHeader({
  icon,
  title,
  trailing,
}: {
  icon: string;
  title: string;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between px-4 pt-4 pb-3">
      <div className="flex items-center gap-2">
        <span aria-hidden className="text-[16px] leading-none">
          {icon}
        </span>
        <h2 className="text-[15px] font-semibold text-gray-900 tracking-[-0.01em]">{title}</h2>
      </div>
      {trailing && <div className="flex items-center gap-1.5">{trailing}</div>}
    </div>
  );
}

/**
 * 라벨-값 한 줄. 값이 없으면 회색 placeholder, 있으면 강조.
 * 모바일 폭에서는 값이 길면 자동으로 줄바꿈된다.
 */
function InfoRow({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value?: string | null;
  emphasize?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 px-4 py-2.5 border-t border-gray-100">
      <div className="min-w-[84px] shrink-0 text-[13px] leading-relaxed text-gray-500">{label}</div>
      {value ? (
        <div
          className={`flex-1 text-[14px] leading-relaxed ${
            emphasize ? 'font-semibold text-gray-900' : 'text-gray-900'
          }`}
        >
          {value}
        </div>
      ) : (
        <div className="flex-1 text-[14px] leading-relaxed text-gray-400">미입력</div>
      )}
    </div>
  );
}

/**
 * 카드 하위의 서브-블록 헤더 (출발지/도착지, 이사 유형의 sub-block 등).
 *
 * `separated` — 앞 섹션과의 간격이 필요할 때 사용. 타이포를 강조해 섹션 경계를 표시한다.
 */
function SubHeader({ children, separated }: { children: string; separated?: boolean }) {
  return (
    <div
      className={`px-4 pb-1 text-[13px] font-semibold tracking-[-0.01em] ${
        separated ? 'mt-3 pt-4 text-gray-700' : 'pt-3 text-gray-700'
      }`}
    >
      {children}
    </div>
  );
}

/** 카드/행 우측에 표시되는 작은 수정 버튼. */
function EditButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="rounded-md px-2 py-0.5 text-[12px] font-medium text-blue-600 hover:bg-blue-50 active:bg-blue-100 transition-colors"
    >
      수정
    </button>
  );
}

/** 짐 N개 / 영상 / 미입력 등 동일한 톤의 inline Tag. */
function CountTag({
  variant,
  children,
}: {
  variant: 'info' | 'warn' | 'success' | 'neutral';
  children: React.ReactNode;
}) {
  return (
    <Tag variant={variant} content="text">
      {children}
    </Tag>
  );
}

// ─── 편집 콜백 타입 ───────────────────────────────────────────────────────────

export interface ReviewEditRoomInfo {
  sessionId: string;
  roomLabel: string;
  initialMovingItems?: MovingItemRequest[];
}

export interface ReviewEditAdditionalSpaceInfo {
  sessionId: string;
  spaceLabel: string;
  initialMovingItems?: MovingItemRequest[];
  canRecordMovingItems?: boolean;
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface CounselReviewScreenProps {
  counsel: VisitCounselView;
  sketchingSession: SketchingSession | null;
  roomObserving: RoomObservingSession[];
  additionalSpaceObserving: AdditionalSpaceObservingSession[];
  movingTypeSpecs?: MovingTypeSpecificationResponse[] | null;
  /**
   * 하단 sticky CTA 클릭. 기본 라벨은 "추가공간 촬영하기" (additional-spaces stage 진입용)이며,
   * `done && !ready` 같이 다른 컨텍스트에서 재사용 시 `proceedLabel` 로 라벨을 교체한다.
   *
   * post-progress 리뷰에서는 인테이크 세션 완료 같은 비동기 작업이 동반되므로
   * `Promise<void>` 반환도 허용한다. 진행 상태는 `isProceeding` 으로 표시한다.
   */
  onProceed: () => void | Promise<void>;
  /** 좌상단 뒤로가기 — 상담 상세로 복귀 */
  onBack?: () => void;
  /** 하단 CTA 라벨. 기본값 "추가공간 촬영하기". */
  proceedLabel?: string;
  /**
   * CTA 진행 중 여부 — true 이면 버튼 disabled + 라벨을 "진행 중..." 으로 대체한다.
   * post-progress 리뷰에서 `completeIntakeSession` 호출 동안 활성화한다.
   */
  isProceeding?: boolean;
  /**
   * 인테이크 섹션 수정 — 카드 헤더 "수정" 버튼 클릭 시 호출.
   * 미제공 시 수정 버튼을 렌더하지 않는다.
   */
  onEditIntakeSection?: (
    section: 'date' | 'departure' | 'arrival' | 'packing' | 'movingType' | 'customer'
  ) => void;
  /**
   * 방별 짐 & 영상 세션 수정 — 방 카드 "수정" 클릭 시 호출.
   * 미제공 시 수정 버튼을 렌더하지 않는다.
   */
  onEditRoom?: (info: ReviewEditRoomInfo) => void;
  /**
   * 추가공간 세션 수정 — 추가공간 행 "수정" 클릭 시 호출.
   * 미제공 시 수정 버튼을 렌더하지 않는다.
   */
  onEditAdditionalSpace?: (info: ReviewEditAdditionalSpaceInfo) => void;
}

// ─── 메인 컴포넌트 ────────────────────────────────────────────────────────────

/**
 * "상담 진행하기(intake)" 시퀀스(이사일 ~ 고객요청사항)가 모두 끝난 직후 표시되는 풀스크린 리뷰.
 *
 * 디자인 컨셉:
 * - 회색(bg-gray-50) 캔버스 위에 흰 카드들이 정보 단위로 분리되어 떠 있는 구조.
 * - 카드 헤더: 아이콘 + 제목 + 상태 Tag (완료/n개 등) 의 3-스팟 패턴.
 * - 카드 내부: 라벨(text-gray-500) / 값(text-gray-900) 의 키-값 row.
 * - 방·추가공간: 짐 카운트 / 영상 / 완료 상태를 Tag 로 정형화된 시각 어휘로 표현.
 *
 * 이전 버전 대비 개선점:
 *   1. divider 1px 만으로 구분되던 평면 구조 → 카드 시각 계층 도입.
 *   2. raw "{...}" JSON 노출(예: delayReason {"choice":"OTHER"}) →
 *      `summarizeMovingTypeAttributeValue` 에 fallback 파서를 추가해 사용자 친화 문자열로 정규화.
 *   3. 단순 텍스트 → 디자인 시스템 Tag 컴포넌트로 일관된 상태 어휘.
 */
export function CounselReviewScreen({
  counsel,
  sketchingSession,
  roomObserving,
  additionalSpaceObserving,
  movingTypeSpecs,
  onProceed,
  onBack,
  proceedLabel = '추가공간 촬영하기',
  isProceeding = false,
  onEditIntakeSection,
  onEditRoom,
  onEditAdditionalSpace,
}: CounselReviewScreenProps) {
  const { specs } = useMovingItemSpecs();
  const specMap = useMemo(() => new Map(specs.map((s) => [s.id, s])), [specs]);

  const rooms = useMemo(
    () => (sketchingSession?.rooms ?? []).filter((r) => !r.isAdditionalSpace),
    [sketchingSession]
  );

  const sortedAdditional = useMemo(
    () => sortAdditionalSpaceObservingSessions(additionalSpaceObserving),
    [additionalSpaceObserving]
  );

  // ── 데이터 추출 ───────────────────────────────────────────────────────────
  const departure = getIntakeDeparture(counsel);
  const arrival = getIntakeArrival(counsel);
  const movingDate = getIntakeMovingDate(counsel);
  const packing = getIntakePacking(counsel);
  const customerRequests = getIntakeCustomerRequests(counsel);
  const movingTypeSummary = getIntakeChecklistDescription(counsel, 'movingType', {
    movingTypeSpecs,
  });
  const movingTypeDetailBlocks = useMemo(
    () => buildMovingTypeIntakeReviewBlocks(counsel?.movingTypeIntake, movingTypeSpecs),
    [counsel?.movingTypeIntake, movingTypeSpecs]
  );
  const hasMovingTypeIntake = hasIntakeMovingTypeRecorded(counsel);

  // ── 방·추가공간 카운트 계산 ───────────────────────────────────────────────
  const roomStats = useMemo(() => {
    let done = 0;
    for (const room of rooms) {
      const session = resolveRoomObservingSessionForSketchRoom(room, rooms, roomObserving);
      const movingConfirmed = hasObservingSessionMovingItemsConfirmed(session);
      const hasVideo = !!session?.video;
      if (movingConfirmed && hasVideo) done += 1;
    }
    return { done, total: rooms.length };
  }, [rooms, roomObserving]);

  const additionalStats = useMemo(() => {
    let done = 0;
    for (const space of sortedAdditional) {
      const requiresItems = space.canRecordMovingItems === true;
      const hasVideo = !!space.video;
      const items = space.movingItems ?? [];
      const isComplete = requiresItems
        ? hasVideo &&
          hasObservingSessionMovingItemsConfirmed(space) &&
          items.every((it) => it.serviceSet != null)
        : hasVideo;
      if (isComplete) done += 1;
    }
    return { done, total: sortedAdditional.length };
  }, [sortedAdditional]);

  // ── 진행 정보 카드 valuemap ───────────────────────────────────────────────
  const packingLabel = packing?.method ? (PACKING_LABELS[packing.method] ?? packing.method) : null;

  return (
    <div className="flex h-full w-full flex-col bg-gray-50">
      <PageHeader title="상담 현황 리뷰" onBack={onBack} />

      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col gap-3 px-4 pt-3 pb-6">
          {/* 1) 이사 개요 */}
          <Card>
            <CardHeader
              icon="📅"
              title="이사 개요"
              trailing={
                onEditIntakeSection && <EditButton onClick={() => onEditIntakeSection('date')} />
              }
            />
            <InfoRow label="이사일" value={movingDate ?? null} emphasize />
          </Card>

          {/* 2) 이사 유형 */}
          <Card>
            <CardHeader
              icon="🏷️"
              title="이사 유형"
              trailing={
                <>
                  {hasMovingTypeIntake && movingTypeSummary ? (
                    <CountTag variant="info">기록됨</CountTag>
                  ) : (
                    <CountTag variant="warn">미입력</CountTag>
                  )}
                  {onEditIntakeSection && hasMovingTypeIntake && movingTypeSummary && (
                    <EditButton onClick={() => onEditIntakeSection('movingType')} />
                  )}
                </>
              }
            />
            {!hasMovingTypeIntake || !movingTypeSummary ? (
              <div className="px-4 pt-1 pb-4 text-[14px] text-gray-400">
                이사 유형이 아직 기록되지 않았어요.
              </div>
            ) : (
              <div className="px-4 pt-1 pb-4">
                <div className="rounded-xl bg-gray-50 px-3 py-2 text-[14px] font-semibold text-gray-900">
                  {movingTypeSummary}
                </div>
                {movingTypeDetailBlocks.map((block) => (
                  <div key={block.code} className="mt-3">
                    <div className="mb-2 flex items-center gap-1.5">
                      <span className="h-1 w-1 rounded-full bg-gray-400" />
                      <span className="text-[12px] font-semibold text-gray-700">{block.title}</span>
                    </div>
                    {block.rows.length === 0 ? (
                      <div className="pl-3 text-[13px] leading-relaxed text-gray-400">
                        세부 항목 없음
                      </div>
                    ) : (
                      <div className="rounded-xl border border-gray-100 bg-white">
                        {block.rows.map((row, ri) => (
                          <div
                            key={`${block.code}-${ri}`}
                            className={`flex items-start gap-3 px-3 py-2 ${
                              ri > 0 ? 'border-t border-gray-100' : ''
                            }`}
                          >
                            <div className="min-w-[88px] shrink-0 text-[13px] leading-relaxed text-gray-500">
                              {row.label}
                            </div>
                            <div className="flex-1 text-[14px] leading-relaxed text-gray-900">
                              {row.value}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* 3) 출발지 / 도착지 */}
          <Card>
            <CardHeader
              icon="📍"
              title="출발지 / 도착지"
              trailing={
                onEditIntakeSection && (
                  <EditButton onClick={() => onEditIntakeSection('departure')} />
                )
              }
            />

            <SubHeader>출발지</SubHeader>
            <InfoRow
              label="주소"
              value={departure?.address ? formatStructuredAddress(departure.address) : null}
            />
            <InfoRow
              label="운반방식"
              value={
                departure?.movingMethod?.type
                  ? (SPACE_TYPE_LABELS[departure.movingMethod.type] ?? departure.movingMethod.type)
                  : null
              }
            />

            <SubHeader separated>도착지</SubHeader>
            <InfoRow
              label="주소"
              value={arrival?.address ? formatStructuredAddress(arrival.address) : null}
            />
            <InfoRow
              label="평수"
              value={
                typeof arrival?.supplyArea === 'number' && arrival.supplyArea > 0
                  ? `${arrival.supplyArea}평`
                  : null
              }
            />
            <InfoRow
              label="운반방식"
              value={
                arrival?.movingMethod?.type
                  ? (SPACE_TYPE_LABELS[arrival.movingMethod.type] ?? arrival.movingMethod.type)
                  : null
              }
            />

            <SubHeader separated>포장</SubHeader>
            <InfoRow label="포장방식" value={packingLabel} />
            <div className="pb-1" />
          </Card>

          {/* 4) 방별 짐 & 영상 */}
          <Card>
            <CardHeader
              icon="🛋️"
              title="방별 짐 & 영상"
              trailing={
                rooms.length > 0 && (
                  <CountTag variant={roomStats.done === roomStats.total ? 'success' : 'warn'}>
                    {roomStats.done}/{roomStats.total} 완료
                  </CountTag>
                )
              }
            />
            {rooms.length === 0 ? (
              <div className="px-4 pt-1 pb-4 text-[14px] text-gray-400">
                집 스케치가 아직 기록되지 않았어요.
              </div>
            ) : (
              <div className="px-4 pb-4">
                <div className="space-y-2">
                  {rooms.map((room, idx) => (
                    <RoomReviewCard
                      key={idx}
                      room={room}
                      rooms={rooms}
                      roomObserving={roomObserving}
                      specMap={specMap}
                      onEdit={onEditRoom}
                    />
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* 5) 추가공간 */}
          <Card>
            <CardHeader
              icon="📦"
              title="추가공간"
              trailing={
                sortedAdditional.length > 0 && (
                  <CountTag
                    variant={additionalStats.done === additionalStats.total ? 'success' : 'warn'}
                  >
                    {additionalStats.done}/{additionalStats.total} 완료
                  </CountTag>
                )
              }
            />
            {sortedAdditional.length === 0 ? (
              <div className="px-4 pt-1 pb-4 text-[14px] text-gray-400">추가공간이 없어요.</div>
            ) : (
              <div className="px-4 pb-4">
                <div className="space-y-2">
                  {sortedAdditional.map((space, idx) => (
                    <AdditionalSpaceReviewRow
                      key={idx}
                      space={space}
                      onEdit={onEditAdditionalSpace}
                    />
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* 6) 고객 요청사항 */}
          <Card>
            <CardHeader
              icon="💬"
              title="고객 요청사항"
              trailing={
                <>
                  {customerRequests ? (
                    <CountTag variant="neutral">
                      {(customerRequests.questions?.length ?? 0) +
                        (customerRequests.paidQuoteRequests?.length ?? 0)}
                      건
                    </CountTag>
                  ) : (
                    <CountTag variant="warn">미입력</CountTag>
                  )}
                  {onEditIntakeSection && (
                    <EditButton onClick={() => onEditIntakeSection('customer')} />
                  )}
                </>
              }
            />
            {!customerRequests ? (
              <div className="px-4 pt-1 pb-4 text-[14px] text-gray-400">
                요청사항이 아직 기록되지 않았어요.
              </div>
            ) : (
              <div className="px-4 pb-4 space-y-3">
                <RequestList
                  label="일반 요청"
                  items={customerRequests.questions ?? []}
                  emptyText="일반 요청 없음"
                />
                <RequestList
                  label="유료 견적 요청"
                  items={customerRequests.paidQuoteRequests ?? []}
                  emptyText="유료 견적 요청 없음"
                  emphasize
                />
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* 하단 sticky CTA — 기본은 추가공간 촬영 진입, `proceedLabel` 로 다른 컨텍스트에도 재사용. */}
      <div className="w-full bg-white pt-3 px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] border-t border-gray-100">
        <Button
          variant="primary"
          size="l"
          shape="rectangle"
          width="fixed"
          onPress={onProceed}
          disabled={isProceeding}
        >
          {isProceeding ? '진행 중...' : proceedLabel}
        </Button>
      </div>
    </div>
  );
}

// ─── 방 단위 카드 ─────────────────────────────────────────────────────────────

/**
 * 메인 방 한 행. 이름 + 짐/영상 상태 Tag + (펼침 영역의) 짐 리스트.
 * 짐이 있으면 들여쓰기된 sub-list 로 노출 — `formatItemDisplay` 가 specMap 기반으로
 * "스펙 (속성 ...)" 포맷을 만든다.
 */
function RoomReviewCard({
  room,
  rooms,
  roomObserving,
  specMap,
  onEdit,
}: {
  room: SketchingSessionRoom;
  rooms: SketchingSessionRoom[];
  roomObserving: RoomObservingSession[];
  specMap: Map<string, Spec>;
  onEdit?: (info: ReviewEditRoomInfo) => void;
}) {
  const session = resolveRoomObservingSessionForSketchRoom(room, rooms, roomObserving);
  const items = session?.movingItems ?? [];
  const hasItems = items.length > 0 || (session?.movingItemSpecIds?.length ?? 0) > 0;
  const movingConfirmed = hasObservingSessionMovingItemsConfirmed(session);
  const hasVideo = !!session?.video;
  const isComplete = movingConfirmed && hasVideo;
  const singleton = isSingletonRoomType(room, rooms);
  const roomLabel = formatSketchingRoomLabelSpaced(
    room,
    singleton ? { singleton: true } : undefined
  );
  const itemCount = items.length > 0 ? items.length : (session?.movingItemSpecIds?.length ?? 0);

  const handleEdit = React.useCallback(() => {
    if (!onEdit || !session) return;
    const initialMovingItems =
      items.length > 0
        ? items
            .filter((it) => it.serviceSet != null)
            .map((it) => ({
              specId: it.specId,
              attributes: it.attributes ?? {},
              serviceSet:
                it.serviceSet as import('@/entities/visit-counsel/model/room-observing.types').ServiceSet,
            }))
        : undefined;
    onEdit({ sessionId: session.id, roomLabel, initialMovingItems });
  }, [onEdit, session, items, roomLabel]);

  return (
    <div className="rounded-xl border border-gray-100 bg-white px-3 py-2.5">
      <div className="flex items-center gap-2">
        <div className="flex-1 text-[14px] font-semibold text-gray-900">{roomLabel}</div>
        <CountTag variant={hasItems ? 'neutral' : 'warn'}>
          {hasItems ? `짐 ${itemCount}개` : '짐 없음'}
        </CountTag>
        <CountTag variant={hasVideo ? 'success' : 'warn'}>{hasVideo ? '영상' : '미촬영'}</CountTag>
        <CountTag variant={isComplete ? 'success' : 'warn'}>
          {isComplete ? '완료' : '미완료'}
        </CountTag>
        {onEdit && session && <EditButton onClick={handleEdit} />}
      </div>

      {items.length > 0 ? (
        <ul className="mt-2 space-y-1 pl-1">
          {items.map((item, itemIdx) => (
            <li
              key={itemIdx}
              className="flex items-center justify-between gap-2 text-[13px] leading-relaxed"
            >
              <span className="flex items-center gap-1.5 text-gray-700">
                <span aria-hidden className="text-gray-300">
                  •
                </span>
                {formatItemDisplay(item, specMap)}
              </span>
              {item.serviceSet && (
                <span className="shrink-0 rounded-md bg-gray-50 px-2 py-0.5 text-[12px] font-medium text-gray-600">
                  {SERVICE_SET_LABELS[item.serviceSet] ?? item.serviceSet}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : movingConfirmed ? (
        <div className="mt-2 pl-1 text-[12px] text-gray-400">짐 없음으로 기록됨</div>
      ) : (
        <div className="mt-2 pl-1 text-[12px] text-orange-700">아직 기록되지 않음</div>
      )}
    </div>
  );
}

// ─── 추가공간 단위 행 ─────────────────────────────────────────────────────────

function AdditionalSpaceReviewRow({
  space,
  onEdit,
}: {
  space: AdditionalSpaceObservingSession;
  onEdit?: (info: ReviewEditAdditionalSpaceInfo) => void;
}) {
  const label = SPACE_TYPE_LABELS[space.spaceType] ?? space.spaceType;
  const hasVideo = !!space.video;
  const requiresItems = space.canRecordMovingItems === true;
  const items = space.movingItems ?? [];
  const hasItems = items.length > 0 || (space.movingItemSpecIds?.length ?? 0) > 0;
  const itemCount = items.length > 0 ? items.length : (space.movingItemSpecIds?.length ?? 0);
  const isComplete = requiresItems
    ? hasVideo &&
      hasObservingSessionMovingItemsConfirmed(space) &&
      items.every((it) => it.serviceSet != null)
    : hasVideo;

  const handleEdit = React.useCallback(() => {
    if (!onEdit) return;
    const initialMovingItems =
      items.length > 0
        ? items
            .filter((it) => it.serviceSet != null)
            .map((it) => ({
              specId: it.specId,
              attributes: it.attributes ?? {},
              serviceSet:
                it.serviceSet as import('@/entities/visit-counsel/model/room-observing.types').ServiceSet,
            }))
        : undefined;
    onEdit({
      sessionId: space.id,
      spaceLabel: label,
      initialMovingItems,
      canRecordMovingItems: space.canRecordMovingItems ?? undefined,
    });
  }, [onEdit, space.id, label, space.canRecordMovingItems, items]);

  return (
    <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-3 py-2.5">
      <div className="flex-1 text-[14px] font-semibold text-gray-900">{label}</div>
      {requiresItems && (
        <CountTag variant={hasItems ? 'neutral' : 'warn'}>
          {hasItems ? `짐 ${itemCount}개` : '짐 없음'}
        </CountTag>
      )}
      <CountTag variant={hasVideo ? 'success' : 'warn'}>{hasVideo ? '영상' : '미촬영'}</CountTag>
      <CountTag variant={isComplete ? 'success' : 'warn'}>
        {isComplete ? '완료' : '미완료'}
      </CountTag>
      {onEdit && <EditButton onClick={handleEdit} />}
    </div>
  );
}

// ─── 고객 요청사항 리스트 ─────────────────────────────────────────────────────

function RequestList({
  label,
  items,
  emptyText,
  emphasize = false,
}: {
  label: string;
  items: string[];
  emptyText: string;
  emphasize?: boolean;
}) {
  if (items.length === 0) {
    return (
      <div>
        <div className="mb-1 text-[12px] font-semibold uppercase tracking-[0.1em] text-gray-400">
          {label}
        </div>
        <div className="text-[13px] text-gray-400">{emptyText}</div>
      </div>
    );
  }
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-gray-500">
          {label}
        </span>
        <span className="text-[12px] text-gray-400">· {items.length}건</span>
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li
            key={i}
            className={`flex items-start gap-2 rounded-lg px-3 py-2 text-[13px] leading-relaxed ${
              emphasize ? 'bg-orange-50 text-orange-700' : 'bg-gray-50 text-gray-900'
            }`}
          >
            <span aria-hidden className={emphasize ? 'text-orange-500' : 'text-gray-400'}>
              {emphasize ? '₩' : '•'}
            </span>
            <span className="flex-1">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
